import { useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { fetchService } from "../../api/public.api.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { RichText } from "../../components/shared/RichText.jsx";
import { InquiryForm } from "../../components/forms/InquiryForm.jsx";
import { SEO } from "../../lib/seo.jsx";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const {
    data: service,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public", "service", slug],
    queryFn: () => fetchService(slug),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isError) return <Navigate to="/services" replace />;
  if (isLoading || !service) {
    return (
      <Container className="py-20">
        <div className="h-72 animate-pulse rounded-2xl bg-bg-elevated" />
      </Container>
    );
  }

  return (
    <>
      <SEO
        title={service.title}
        description={service.seo?.description ?? service.shortDescription}
        image={service.heroImage}
      />

      <PageHero
        eyebrow="Service"
        title={service.title}
        description={service.shortDescription}
        breadcrumbs={[{ label: "Services", to: "/services" }, { label: service.title }]}
      />

      <Container className="-mt-6 mb-12 lg:-mt-12">
        <div className="aspect-[16/9] overflow-hidden rounded-2xl border border-border-subtle">
          <img
            src={imageOrPlaceholder(service.heroImage, {
              pool: "design",
              key: service.slug ?? service._id ?? service.title,
            })}
            onError={imgErrorFallback(service.slug ?? service._id ?? service.title)}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      </Container>

      <Container className="pb-16 lg:pb-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:gap-12">
          <article className="min-w-0">
            <RichText html={service.content} />

            {service.ctaLabel && service.ctaLink && (
              <div className="mt-10">
                <Link to={service.ctaLink} className="btn-primary">
                  {service.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </article>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="space-y-4 rounded-2xl border border-border-subtle bg-bg-elevated p-6">
              <div>
                <p className="eyebrow">Get in touch</p>
                <h2 className="mt-2 font-display text-xl text-text-primary">
                  Enquire about {service.title}
                </h2>
              </div>
              <InquiryForm
                formType="contact_general"
                prefill={{ service: service.title, serviceSlug: service.slug }}
              />
            </div>
          </aside>
        </div>
      </Container>
    </>
  );
}
